import styled from '@tanium/react-emotion';
import { css } from '@tanium/style-system';
import {
  ArrowBottomRightPlacement,
  arrowSize,
  ArrowTopLeftPlacement,
  BackdropFilter,
  Border,
  Fill,
  FillWithAlpha,
} from './themeDefinitions';
import { TooltipProps } from './types';

// Popper applies its own positioning and transforms to the arrow.
// This adjustment makes sure that the arrow is centered where it
// connects to the popper (in cases where the arrow is meant to be
// centered).
const centeringAdjustment = `${-arrowSize / 2}px`;

/**
 * Arrow component to be used in tooltips. Supports four directions of placement.
 */
export const Arrow = styled('div')<Pick<TooltipProps, 'opacity'>>(({ opacity }) =>
  css((theme) => ({
    visibility: 'hidden',
    '::before': {
      backgroundColor: Fill,
      position: 'absolute',
      width: `${arrowSize}px`,
      height: `${arrowSize}px`,
      border: 1,
      borderColor: Border,
      transformOrigin: 'center',
      clipPath: 'polygon(0 0, 100% 0, 100% 100%)',
      visibility: 'visible',
      content: '""',
    },
    ["&[data-placement^='top']" as string]: {
      bottom: ArrowTopLeftPlacement(theme),
      '::before': {
        transform: 'rotate(135deg)',
        left: centeringAdjustment,
      },
    },
    ["&[data-placement^='right']" as string]: {
      left: ArrowBottomRightPlacement(theme),
      '::before': {
        transform: 'rotate(-135deg)',
        top: centeringAdjustment,
      },
    },
    ["&[data-placement^='bottom']" as string]: {
      top: ArrowBottomRightPlacement(theme),
      '::before': {
        transform: 'rotate(-45deg)',
        left: centeringAdjustment,
      },
    },
    ["&[data-placement^='left']" as string]: {
      right: ArrowTopLeftPlacement(theme),
      '::before': {
        transform: 'rotate(45deg)',
        top: centeringAdjustment,
      },
    },
    // We check for backdrop-filter here to keep its translucent background
    // logic in sync with TooltipContentWrapper, but we don't actually
    // apply a backdrop-filter in the arrow as it picks up the wrapper's
    // shadow and becomes too dark.
    ...(BackdropFilter(theme) && {
      [`@supports (backdrop-filter: ${BackdropFilter(theme)})`]: {
        backgroundColor: FillWithAlpha(opacity),
      },
    }),
  })),
);
